import i18n from "@/lib/i18n";

/**
 * Shared types, constants and helpers for the BeraStays Insurance Module.
 */

export type InsuranceStatus =
  | "not_activated"
  | "documents_pending"
  | "ready_to_apply"
  | "submitted"
  | "in_review"
  | "docs_required"
  | "approved"
  | "rejected"
  | "active"
  | "expiring_soon"
  | "expired"
  | "renewal_pending";

export type InsuranceType = "6_months" | "annual";

export type MediaCategory =
  | "sala"
  | "quartos"
  | "cozinha"
  | "eletrodomesticos"
  | "moveis"
  | "video_interior"
  | "outros";

const STATUS_LABEL_DEFAULTS: Record<InsuranceStatus, string> = {
  not_activated: "Seguro não ativado",
  documents_pending: "Documentos pendentes",
  ready_to_apply: "Pronto para aplicar",
  submitted: "Pedido enviado",
  in_review: "Em análise",
  docs_required: "Documentos solicitados",
  approved: "Seguro aprovado",
  rejected: "Seguro recusado",
  active: "Seguro ativo",
  expiring_soon: "A expirar em breve",
  expired: "Seguro expirado",
  renewal_pending: "Renovação enviada",
};

export function getInsuranceStatusLabel(status: InsuranceStatus): string {
  return i18n.t(`insurance.status.${status}`, { defaultValue: STATUS_LABEL_DEFAULTS[status] });
}

export const STATUS_LABEL = new Proxy(STATUS_LABEL_DEFAULTS, {
  get(target, prop: string) {
    if (prop in target) return getInsuranceStatusLabel(prop as InsuranceStatus);
    return undefined;
  },
}) as Record<InsuranceStatus, string>;

export const STATUS_COLOR: Record<InsuranceStatus, { bg: string; text: string; border: string; dot: string }> = {
  not_activated:    { bg: "bg-zinc-100",   text: "text-zinc-700",    border: "border-zinc-200",    dot: "bg-zinc-400" },
  documents_pending:{ bg: "bg-amber-50",   text: "text-amber-800",   border: "border-amber-200",   dot: "bg-amber-500" },
  ready_to_apply:   { bg: "bg-blue-50",    text: "text-blue-800",    border: "border-blue-200",    dot: "bg-blue-500" },
  submitted:        { bg: "bg-indigo-50",  text: "text-indigo-800",  border: "border-indigo-200",  dot: "bg-indigo-500" },
  in_review:        { bg: "bg-violet-50",  text: "text-violet-800",  border: "border-violet-200",  dot: "bg-violet-500" },
  docs_required:    { bg: "bg-orange-50",  text: "text-orange-800",  border: "border-orange-200",  dot: "bg-orange-500" },
  approved:         { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", dot: "bg-emerald-500" },
  rejected:         { bg: "bg-rose-50",    text: "text-rose-800",    border: "border-rose-200",    dot: "bg-rose-500" },
  active:           { bg: "bg-[#E8513D]/8",text: "text-[#B83A28]",   border: "border-[#E8513D]/30",dot: "bg-[#E8513D]" },
  expiring_soon:    { bg: "bg-amber-50",   text: "text-amber-800",   border: "border-amber-200",   dot: "bg-amber-500" },
  expired:          { bg: "bg-zinc-100",   text: "text-zinc-600",    border: "border-zinc-200",    dot: "bg-zinc-400" },
  renewal_pending:  { bg: "bg-violet-50",  text: "text-violet-800",  border: "border-violet-200",  dot: "bg-violet-500" },
};

const CATEGORY_LABEL_DEFAULTS: Record<MediaCategory, string> = {
  sala: "Sala",
  quartos: "Quartos",
  cozinha: "Cozinha",
  eletrodomesticos: "Eletrodomésticos",
  moveis: "Móveis principais",
  video_interior: "Vídeo do interior",
  outros: "Outros documentos",
};

export function getInsuranceCategoryLabel(category: MediaCategory): string {
  return i18n.t(`insurance.category.${category}`, { defaultValue: CATEGORY_LABEL_DEFAULTS[category] });
}

export const CATEGORY_LABEL = new Proxy(CATEGORY_LABEL_DEFAULTS, {
  get(target, prop: string) {
    if (prop in target) return getInsuranceCategoryLabel(prop as MediaCategory);
    return undefined;
  },
}) as Record<MediaCategory, string>;

export const REQUIRED_CATEGORIES: { category: MediaCategory; mediaType: "image" | "video"; label: string }[] = [
  { category: "sala",            mediaType: "image", get label() { return i18n.t("insurance.required.sala", { defaultValue: "Pelo menos 1 foto da sala" }); } },
  { category: "quartos",         mediaType: "image", get label() { return i18n.t("insurance.required.quartos", { defaultValue: "Pelo menos 1 foto dos quartos" }); } },
  { category: "cozinha",         mediaType: "image", get label() { return i18n.t("insurance.required.cozinha", { defaultValue: "Pelo menos 1 foto da cozinha" }); } },
  { category: "moveis",          mediaType: "image", get label() { return i18n.t("insurance.required.moveis", { defaultValue: "Pelo menos 1 foto dos móveis principais" }); } },
  { category: "video_interior",  mediaType: "video", get label() { return i18n.t("insurance.required.video_interior", { defaultValue: "Pelo menos 1 vídeo curto do interior" }); } },
];

const DEFAULT_COVERAGE_INCLUDED_DEFAULTS = [
  "Danos em mobílias",
  "Danos em eletrodomésticos",
  "Danos acidentais causados por hóspedes",
  "Quebras ou avarias comunicadas dentro do prazo",
  "Danos confirmados após análise da seguradora",
];

const DEFAULT_COVERAGE_EXCLUDED_DEFAULTS = [
  "Desgaste natural do imóvel",
  "Danos causados pelo próprio proprietário",
  "Mobílias não declaradas no processo de seguro",
  "Danos sem prova fotográfica ou vídeo",
  "Situações fora das condições da apólice",
  "Informações falsas ou incompletas",
];

function translatedArray(defaults: string[], keyPrefix: string): string[] {
  return new Proxy(defaults, {
    get(target, prop: string) {
      if (/^\d+$/.test(prop)) {
        const idx = Number(prop);
        return i18n.t(`${keyPrefix}.${idx}`, { defaultValue: target[idx] });
      }
      return (target as any)[prop];
    },
  }) as string[];
}

export const DEFAULT_COVERAGE_INCLUDED = translatedArray(DEFAULT_COVERAGE_INCLUDED_DEFAULTS, "insurance.coverage.included");
export const DEFAULT_COVERAGE_EXCLUDED = translatedArray(DEFAULT_COVERAGE_EXCLUDED_DEFAULTS, "insurance.coverage.excluded");

export const SUPPORT = {
  email: "seguro@berastays.com",
  whatsapp: "+244923000000",
};

export type MediaCount = Record<MediaCategory, { images: number; videos: number }>;

export function buildMediaCount(rows: { category: MediaCategory; media_type: "image" | "video" }[]): MediaCount {
  const init = (Object.keys(CATEGORY_LABEL) as MediaCategory[]).reduce((acc, c) => {
    acc[c] = { images: 0, videos: 0 };
    return acc;
  }, {} as MediaCount);
  for (const r of rows) {
    if (r.media_type === "image") init[r.category].images++;
    else init[r.category].videos++;
  }
  return init;
}

export function isReadyToApply(count: MediaCount): { ready: boolean; missing: string[] } {
  const missing: string[] = [];
  for (const req of REQUIRED_CATEGORIES) {
    const c = count[req.category];
    const ok = req.mediaType === "image" ? c.images >= 1 : c.videos >= 1;
    if (!ok) missing.push(req.label);
  }
  return { ready: missing.length === 0, missing };
}

export function deriveOwnerStatus(
  current: InsuranceStatus,
  count: MediaCount
): InsuranceStatus {
  // Auto-derive between not_activated -> documents_pending -> ready_to_apply
  if (current !== "not_activated" && current !== "documents_pending" && current !== "ready_to_apply") {
    return current;
  }
  const { ready } = isReadyToApply(count);
  if (ready) return "ready_to_apply";
  // Has any media -> documents_pending; none -> not_activated
  const hasAny = (Object.values(count) as { images: number; videos: number }[])
    .some((v) => v.images + v.videos > 0);
  return hasAny ? "documents_pending" : "not_activated";
}

export function formatKz(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("pt-AO").format(Math.round(value)) + " Kz";
}

export function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function daysUntil(d: string | Date | null | undefined): number | null {
  if (!d) return null;
  const ms = new Date(d).getTime() - Date.now();
  return Math.ceil(ms / 86_400_000);
}

export function isRenewalAvailable(endDate: string | null | undefined): boolean {
  const days = daysUntil(endDate);
  return days !== null && days <= 10 && days >= 0;
}
